/**
 * MapLibre 3.x PauseablePlacement arma LayerPlacement con
 * `styleLayer.layout.get(...)`. Si `layout` aún no está o la capa se está
 * sacando, tira "Cannot read properties of undefined (reading 'get')" en
 * continuePlacement.
 *
 * El placement puede pausarse a los 2 ms y seguir en el próximo frame.
 * addLayer / removeLayer / setLayoutProperty de un `symbol` tienen que
 * esperar a que ese ciclo suelte.
 *
 * @param {() => void} fn
 * @returns {() => void} cancel
 */
export function afterCadSymbolPlacement(fn) {
  if (typeof fn !== 'function') return () => {};
  const run = () => {
    try { fn(); } catch (_) { /* mapa o estilo a medias */ }
  };
  const raf = typeof window !== 'undefined' ? window.requestAnimationFrame : null;
  if (typeof raf !== 'function') {
    run();
    return () => {};
  }
  let cancelled = false;
  let innerId = 0;
  const outerId = raf(() => {
    innerId = raf(() => {
      if (!cancelled) run();
    });
  });
  return () => {
    cancelled = true;
    if (typeof window.cancelAnimationFrame !== 'function') return;
    window.cancelAnimationFrame(outerId);
    if (innerId) window.cancelAnimationFrame(innerId);
  };
}

const pendingByKey = new Map();

/**
 * Una sola op pendiente por clave: la última gana. Evita que un addLayer
 * viejo (pines visibles) pise al hide del overlay que se abrió en el medio.
 *
 * @param {string} key
 * @param {() => void} fn
 * @returns {() => void} cancel
 */
export function scheduleCadSymbolOp(key, fn) {
  if (!key || typeof fn !== 'function') return () => {};
  cancelCadSymbolOp(key);
  const entry = { cancelled: false, cancel: () => {} };
  pendingByKey.set(key, entry);
  entry.cancel = afterCadSymbolPlacement(() => {
    if (entry.cancelled || pendingByKey.get(key) !== entry) return;
    pendingByKey.delete(key);
    fn();
  });
  return () => {
    entry.cancelled = true;
    if (pendingByKey.get(key) === entry) pendingByKey.delete(key);
    entry.cancel();
  };
}

/**
 * Descarta la op pendiente de esa capa. El teardown la usa para que un
 * add en vuelo no reponga el symbol que se está sacando.
 *
 * @param {string} key
 */
export function cancelCadSymbolOp(key) {
  const entry = pendingByKey.get(key);
  if (!entry) return;
  entry.cancelled = true;
  pendingByKey.delete(key);
  entry.cancel();
}

let cadSymbolOpEpoch = 0;

/** Invalida un teardown diferido si el overlay se volvió a pintar. */
export function bumpCadSymbolOpEpoch() {
  cadSymbolOpEpoch += 1;
  return cadSymbolOpEpoch;
}

/** @param {number} epoch */
export function isCadSymbolOpEpoch(epoch) {
  return epoch === cadSymbolOpEpoch;
}

/**
 * @param {import('maplibre-gl').Map|null|undefined} map
 * @param {string} layerId
 * @returns {boolean}
 */
export function isCadSymbolLayer(map, layerId) {
  if (!map || !layerId) return false;
  try {
    return map.getLayer(layerId)?.type === 'symbol';
  } catch (_) {
    return false;
  }
}
